import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import ReportGrid from 'components/ReportGrid';
import { useRSAKey2KeyReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDateTime } from 'utils/helpers';
import { ReportProps } from '../hooks';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import {
  RSA_KEY2KEY_REPORT_MESSAGE,
  RSA_KEY2KEY_REPORT_TITLE,
  columns,
  selectOptions,
  tableColumnExtensions,
} from './constants';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportFormKey2KeyReport } from 'components/ReportForms/ReportFormKey2Key';
import { reportLogger } from '../reportLogger';
import { valuesConverter } from './helpers';
import { SelectOption } from 'components/types';

export function RSAKey2KeyReportArchived({
  reportPath,
  setErrorMessage,
  setToast,
}: ReportProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    year: urlParams.get('year'),
  };

  const queryEnabled = !!urlParamsValue.year;

  const { isReportFetching, refetchReport, reportData } = useRSAKey2KeyReport(
    urlParamsValue.year,
    queryEnabled,
    handleError
  );

  const rows = useMemo<Row>(() => reportData, [reportData]);

  const [year, setYear] = useState<SelectOption[]>([]);

  const isBusy = isReportFetching;

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.year]);

  useEffect(() => {
    if (urlParamsValue.year != year?.map((e) => e.value)?.join(','))
      setYear(
        selectOptions?.filter((e) =>
          urlParamsValue.year?.split(',').includes(e.value)
        ) || []
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainPageContainer isLoading={isBusy} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={RSA_KEY2KEY_REPORT_TITLE}
          infoMessage={RSA_KEY2KEY_REPORT_MESSAGE}
        />
        <ReportFormKey2KeyReport
          handleSubmit={handleSubmit}
          isBusy={isBusy}
          setYear={setYear}
          year={year}
        />
      </UpperInfoContainer>

      {!isBusy && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <ReportGrid
          rows={rows}
          columns={columns}
          isLoading={isBusy}
          title="RSAKey2KeyReport"
          tableColumnExtensions={tableColumnExtensions}
          headerTitleForSheet="RSA Key2Key Report"
          reportHeaderTagLine={`Report produced on ${formatDateTime(
            new Date()
          )} for the year ${year?.map(({ text }) => text)?.join(', ')}`}
          pdfOrientation='l'
          showAdditionalExportOptions
          valuesConverter={valuesConverter}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (urlParamsValue.year == year?.map((e) => e.value)?.join(','))
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (year != null)
      navigate(
        createReportURL(
          { year: year?.map((e) => e.value)?.join(',') },
          reportPath,
          true
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('RSA Key2Key Report'));
    setToast(true);
  }
}
